import React, { useCallback, useEffect, useRef } from 'react'

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        options: {
          sitekey: string
          theme?: 'auto' | 'light' | 'dark'
          callback: (token: string) => void
          'expired-callback': () => void
          'error-callback': () => void
        }
      ) => string
      reset: (widgetId: string) => void
      remove: (widgetId: string) => void
    }
    onTurnstileLoad?: () => void
  }
}

type Props = {
  siteKey: string
  onToken: (token: string | null) => void
  // Bumped by the form after a rejected submit. Turnstile tokens are single-use and the
  // widget fires its callback once, so a form that clears the spent token without resetting
  // the widget can never obtain another one — the submit button stays disabled until reload.
  resetSignal?: number
}

export const CloudflareTurnstile: React.FC<Props> = ({ siteKey, onToken, resetSignal }) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const onTokenRef = useRef(onToken)
  onTokenRef.current = onToken

  const renderWidget = useCallback(() => {
    if (!containerRef.current || !window.turnstile) return

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      // the auth cards are a dark slab; the widget's light default reads as a hole in it
      theme: 'dark',
      callback: (token: string) => onTokenRef.current(token),
      'expired-callback': () => onTokenRef.current(null),
      'error-callback': () => onTokenRef.current(null)
    })
  }, [siteKey])

  useEffect(() => {
    if (window.turnstile) {
      renderWidget()
    } else {
      // Script not yet loaded — set callback and inject script once
      window.onTurnstileLoad = renderWidget
      if (!document.querySelector('#cf-turnstile-script')) {
        const script = document.createElement('script')
        script.id = 'cf-turnstile-script'
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad'
        script.async = true
        script.defer = true
        document.head.appendChild(script)
      }
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [renderWidget])

  useEffect(() => {
    if (!resetSignal) return // 0 / undefined is the initial render, not a reset request
    if (widgetIdRef.current && window.turnstile) {
      window.turnstile.reset(widgetIdRef.current)
      onTokenRef.current(null)
    }
  }, [resetSignal])

  return <div ref={containerRef} />
}
